/**
 * Dados do modo mock para Equipe, Contratações, Orçamentos e Notificações.
 */
import type { Permission } from '@/api/endpoints/auth';
import type { RoleOption, TeamMember } from '@/api/endpoints/team';
import type { Job, JobApplication } from '@/api/endpoints/careers';
import type { OrcamentoSalvo, Produto } from '@/api/endpoints/orcamentos';
import type { DeviceInsights, Notification } from '@/api/endpoints/notifications';
import type { OrcamentoDraftData } from '@/pages/Orcamentos/draftStore';
import { calcularOrcamento } from '@/pages/Orcamentos/calc';

const diasAtras = (d: number, hora = 10) => {
  const dt = new Date();
  dt.setDate(dt.getDate() - d);
  dt.setHours(hora, 0, 0, 0);
  return dt.toISOString();
};
const diasAFrente = (d: number) => diasAtras(-d);

// ---------------------------------------------------------------------------
// Equipe
// ---------------------------------------------------------------------------

const AREAS: Permission[] = [
  'dashboard',
  'devices',
  'errors',
  'logs',
  'uptime',
  'users',
  'plans',
  'finance',
  'support',
  'careers',
  'orcamentos',
  'notifications',
  'settings',
  'team',
];

export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [...AREAS],
  desenvolvedor: [
    'dashboard',
    'devices',
    'errors',
    'logs',
    'uptime',
    'users',
    'plans',
    'support',
    'careers',
    'settings',
  ],
  marketing: ['dashboard', 'users', 'plans', 'finance', 'support', 'careers', 'orcamentos'],
  comercial: ['dashboard', 'users', 'plans', 'support', 'careers', 'orcamentos'],
  suporte: ['users', 'support', 'uptime'],
  financeiro: ['dashboard', 'plans', 'finance'],
};

export function permissoesDosCargos(roles: string[]): Permission[] {
  const set = new Set<Permission>();
  for (const r of roles) (ROLE_PERMISSIONS[r] ?? []).forEach((p) => set.add(p));
  return [...set];
}

export const teamRoles: RoleOption[] = [
  { key: 'admin', label: 'Administrador' },
  { key: 'desenvolvedor', label: 'Desenvolvedor' },
  { key: 'marketing', label: 'Marketing & Relações' },
  { key: 'comercial', label: 'Comercial / Vendas' },
  { key: 'suporte', label: 'Suporte ao Cliente' },
  { key: 'financeiro', label: 'Financeiro' },
];

const membro = (
  id: number,
  name: string,
  email: string,
  roles: string[],
  extra: Partial<TeamMember> = {}
): TeamMember => ({
  id,
  name,
  email,
  roles,
  permissions: permissoesDosCargos(roles),
  is_active: true,
  must_change_password: false,
  ...extra,
});

export const teamMembers: TeamMember[] = [
  membro(1, 'Oziel Moreira', 'oziel@curraltech.com.br', ['admin'], {
    created_at: '2025-09-02T13:00:00.000Z',
    last_login: diasAtras(0, 8),
  }),
  membro(2, 'Mikael David', 'claude.agent@curraltech.com.br', ['admin'], {
    created_at: '2025-11-16T17:00:00.000Z',
    last_login: diasAtras(0, 9),
  }),
  membro(4, 'Larissa Moura', 'larissa@curraltech.com.br', ['financeiro', 'suporte'], {
    created_at: '2026-02-10T12:00:00.000Z',
    last_login: diasAtras(0, 7),
  }),
  membro(5, 'Rafael Antunes', 'rafael@curraltech.com.br', ['desenvolvedor'], {
    created_at: '2026-03-03T12:00:00.000Z',
    last_login: diasAtras(1, 18),
  }),
  membro(6, 'Camila Duarte', 'camila@curraltech.com.br', ['comercial', 'suporte'], {
    created_at: '2026-04-22T12:00:00.000Z',
    last_login: diasAtras(0, 11),
  }),
  membro(7, 'Bruno Tavares', 'bruno@curraltech.com.br', ['marketing'], {
    created_at: '2026-07-14T12:00:00.000Z',
    last_login: diasAtras(4, 15),
  }),
  membro(8, 'Patrícia Lemos', 'patricia@curraltech.com.br', ['suporte'], {
    created_at: diasAtras(2, 14),
    must_change_password: true,
  }),
  membro(3, 'Henrique Salgado', 'henrique@curraltech.com.br', ['comercial'], {
    created_at: '2025-12-01T12:00:00.000Z',
    last_login: '2026-05-30T16:00:00.000Z',
    is_active: false,
  }),
];

// ---------------------------------------------------------------------------
// Contratações
// ---------------------------------------------------------------------------

export const careerJobs: Job[] = [
  {
    id: 1,
    slug: 'desenvolvedor-flutter-pleno',
    titulo: 'Desenvolvedor Flutter Pleno',
    area: 'Engenharia',
    tipoContrato: 'PJ',
    local: 'Remoto (Brasil)',
    resumo: 'Evoluir o aplicativo de campo usado por produtores em fazendas sem internet.',
    descricao:
      'Você vai trabalhar no aplicativo Curral Tech, que funciona offline no curral, conversa com leitores RFID por Bluetooth e sincroniza com a nossa API quando volta o sinal.',
    responsabilidades: [
      'Desenvolver telas e fluxos de manejo no app Flutter',
      'Cuidar da sincronização offline e da integridade do SQLite local',
      'Integrar leitores RFID via Bluetooth Low Energy',
      'Revisar código e escrever testes',
    ],
    requisitos: [
      'Experiência com Flutter e Dart em produção',
      'Conhecimento de gerenciamento de estado (GetX, Bloc ou Riverpod)',
      'Vivência com SQLite e apps offline-first',
      'Desejável: experiência com Bluetooth',
    ],
    processo: 'Conversa inicial, desafio técnico curto e entrevista com o time.',
    status: 'aberta',
    ordem: 1,
    candidaturasCount: 4,
    createdAt: diasAtras(24),
  },
  {
    id: 2,
    slug: 'analista-de-suporte-ao-produtor',
    titulo: 'Analista de Suporte ao Produtor',
    area: 'Suporte',
    tipoContrato: 'CLT',
    local: 'Campo Grande, MS (híbrido)',
    resumo: 'Atender produtores pelo WhatsApp e ajudar na implantação do sistema nas fazendas.',
    descricao:
      'Atendimento consultivo a produtores rurais e suas equipes, com foco em resolver na primeira conversa e registrar o que precisa virar melhoria no produto.',
    responsabilidades: [
      'Atender clientes pela Central de Atendimento',
      'Conduzir implantações remotas com produtores novos',
      'Registrar bugs e sugestões para o time de produto',
    ],
    requisitos: [
      'Boa comunicação escrita',
      'Vivência com o dia a dia de fazenda é um diferencial',
      'Facilidade com aplicativos e tecnologia',
    ],
    processo: 'Triagem do currículo, entrevista e simulação de atendimento.',
    status: 'aberta',
    ordem: 2,
    candidaturasCount: 3,
    createdAt: diasAtras(12),
  },
  {
    id: 3,
    slug: 'consultor-comercial-agro',
    titulo: 'Consultor Comercial Agro',
    area: 'Comercial',
    tipoContrato: 'PJ + comissão',
    local: 'Mato Grosso (viagens)',
    resumo: 'Levar a Curral Tech para fazendas de cria, recria e engorda no Mato Grosso.',
    descricao:
      'Prospecção e fechamento com produtores de médio e grande porte, demonstrações em campo e acompanhamento pós-venda junto ao suporte.',
    responsabilidades: [
      'Prospectar e visitar fazendas da região',
      'Montar orçamentos de kit RFID e plano de software',
      'Acompanhar a implantação dos clientes fechados',
    ],
    requisitos: [
      'Experiência em vendas no agro',
      'CNH B e disponibilidade para viagens',
      'Conhecimento de pecuária de corte',
    ],
    processo: 'Entrevista com o comercial e conversa com os sócios.',
    status: 'aberta',
    ordem: 3,
    candidaturasCount: 2,
    createdAt: diasAtras(5),
  },
  {
    id: 4,
    slug: 'designer-de-produto',
    titulo: 'Designer de Produto',
    area: 'Produto & Design',
    tipoContrato: 'PJ',
    local: 'Remoto (Brasil)',
    resumo: 'Desenhar experiências simples para quem usa o celular no meio do curral.',
    descricao:
      'Pesquisa com produtores, protótipos e interfaces do app e dos painéis web, com atenção a uso sob sol forte, luvas e pouca conectividade.',
    responsabilidades: [
      'Conduzir entrevistas e testes de usabilidade',
      'Criar fluxos e protótipos no Figma',
      'Manter o design system',
    ],
    requisitos: ['Portfólio com produto digital', 'Experiência com design system'],
    processo: 'Análise de portfólio, apresentação de case e entrevista.',
    status: 'fechada',
    ordem: 4,
    candidaturasCount: 1,
    createdAt: diasAtras(70),
  },
];

export const careerApplications: JobApplication[] = [
  {
    id: 101,
    tipo: 'vaga',
    vagaId: 1,
    vagaTitulo: 'Desenvolvedor Flutter Pleno',
    nome: 'Gustavo Henrique Rocha',
    whatsapp: '+5567998123456',
    email: 'gustavo.rocha@email.com',
    area: 'Engenharia',
    linkedin: 'https://www.linkedin.com/in/gustavo-rocha-dev',
    mensagem: 'Trabalho com Flutter há 3 anos e já desenvolvi um app offline para cooperativa.',
    status: 'novo',
    createdAt: diasAtras(1, 14),
  },
  {
    id: 102,
    tipo: 'vaga',
    vagaId: 1,
    vagaTitulo: 'Desenvolvedor Flutter Pleno',
    nome: 'Juliana Martins Prado',
    whatsapp: '+5511987654321',
    email: 'juliana.prado@email.com',
    area: 'Engenharia',
    linkedin: 'https://www.linkedin.com/in/julianaprado',
    mensagem: null,
    status: 'em_analise',
    createdAt: diasAtras(6, 9),
  },
  {
    id: 103,
    tipo: 'vaga',
    vagaId: 1,
    vagaTitulo: 'Desenvolvedor Flutter Pleno',
    nome: 'Diego Albuquerque',
    whatsapp: '+5562991234567',
    email: 'diego.alb@email.com',
    area: 'Engenharia',
    linkedin: null,
    mensagem: 'Tenho experiência com BLE em Flutter integrando balanças.',
    status: 'contatado',
    createdAt: diasAtras(10, 16),
  },
  {
    id: 104,
    tipo: 'vaga',
    vagaId: 1,
    vagaTitulo: 'Desenvolvedor Flutter Pleno',
    nome: 'Renan Oliveira Costa',
    whatsapp: '+5565992345678',
    email: 'renan.costa@email.com',
    area: 'Engenharia',
    linkedin: null,
    mensagem: null,
    status: 'arquivado',
    createdAt: diasAtras(18, 11),
  },
  {
    id: 105,
    tipo: 'vaga',
    vagaId: 2,
    vagaTitulo: 'Analista de Suporte ao Produtor',
    nome: 'Amanda Ferreira Lima',
    whatsapp: '+5567993456789',
    email: 'amanda.lima@email.com',
    area: 'Suporte',
    linkedin: 'https://www.linkedin.com/in/amandaflima',
    mensagem: 'Cresci em fazenda em Ribas do Rio Pardo e trabalho com atendimento há 4 anos.',
    status: 'novo',
    createdAt: diasAtras(0, 9),
  },
  {
    id: 106,
    tipo: 'vaga',
    vagaId: 2,
    vagaTitulo: 'Analista de Suporte ao Produtor',
    nome: 'Thiago Moreira Santos',
    whatsapp: '+5567994567890',
    email: 'thiago.santos@email.com',
    area: 'Suporte',
    linkedin: null,
    mensagem: null,
    status: 'em_analise',
    createdAt: diasAtras(3, 13),
  },
  {
    id: 107,
    tipo: 'vaga',
    vagaId: 2,
    vagaTitulo: 'Analista de Suporte ao Produtor',
    nome: 'Bianca Rezende',
    whatsapp: '+5567995678901',
    email: 'bianca.rezende@email.com',
    area: 'Suporte',
    linkedin: null,
    mensagem: 'Sou técnica agropecuária e gosto muito de tecnologia.',
    status: 'novo',
    createdAt: diasAtras(2, 10),
  },
  {
    id: 108,
    tipo: 'vaga',
    vagaId: 3,
    vagaTitulo: 'Consultor Comercial Agro',
    nome: 'Marcelo Antunes Ribeiro',
    whatsapp: '+5566996789012',
    email: 'marcelo.ribeiro@email.com',
    area: 'Comercial',
    linkedin: 'https://www.linkedin.com/in/marceloribeiro-agro',
    mensagem: 'Oito anos vendendo nutrição animal no norte do Mato Grosso.',
    status: 'contatado',
    createdAt: diasAtras(4, 15),
  },
  {
    id: 109,
    tipo: 'vaga',
    vagaId: 3,
    vagaTitulo: 'Consultor Comercial Agro',
    nome: 'Fábio Nogueira',
    whatsapp: '+5565997890123',
    email: 'fabio.nogueira@email.com',
    area: 'Comercial',
    linkedin: null,
    mensagem: null,
    status: 'novo',
    createdAt: diasAtras(1, 17),
  },
  {
    id: 110,
    tipo: 'vaga',
    vagaId: 4,
    vagaTitulo: 'Designer de Produto',
    nome: 'Letícia Campos',
    whatsapp: '+5541998901234',
    email: 'leticia.campos@email.com',
    area: 'Produto & Design',
    linkedin: 'https://www.linkedin.com/in/leticiacampos-ux',
    mensagem: 'Portfólio com cases de apps de campo para o setor sucroenergético.',
    status: 'arquivado',
    createdAt: diasAtras(55, 10),
  },
  {
    id: 111,
    tipo: 'banco',
    vagaId: null,
    vagaTitulo: null,
    nome: 'Rodrigo Pacheco',
    whatsapp: '+5567999012345',
    email: 'rodrigo.pacheco@email.com',
    area: 'Campo & Agro',
    linkedin: null,
    mensagem: 'Zootecnista, quero trabalhar com implantação de RFID nas fazendas.',
    status: 'novo',
    createdAt: diasAtras(2, 8),
  },
  {
    id: 112,
    tipo: 'banco',
    vagaId: null,
    vagaTitulo: null,
    nome: 'Carolina Batista',
    whatsapp: '+5561990123456',
    email: 'carolina.batista@email.com',
    area: 'Engenharia',
    linkedin: 'https://www.linkedin.com/in/carolbatista',
    mensagem: 'Desenvolvedora Node.js, acompanho a Curral Tech desde o lançamento.',
    status: 'em_analise',
    createdAt: diasAtras(15, 12),
  },
];

// ---------------------------------------------------------------------------
// Orçamentos
// ---------------------------------------------------------------------------

export const orcamentoProdutos: Produto[] = [
  {
    id: 1,
    categoria: 'leitor',
    nome: 'Leitor bastão RFID',
    modelo: 'RS420',
    origem: 'alibaba',
    fornecedor: 'Shenzhen Hopeland Technologies',
    moedaCusto: 'USD',
    custoUsd: 185,
    custoBrl: 0,
    pesoG: 620,
    comprimentoCm: 62,
    larguraCm: 9,
    alturaCm: 7,
    specs: ['HDX e FDX-B', 'Bluetooth 4.2', 'Bateria de 12 horas', 'Memória para 10 mil leituras'],
    imagemUrl: '',
    ativo: true,
    ordem: 1,
  },
  {
    id: 2,
    categoria: 'leitor',
    nome: 'Leitor de mão compacto',
    modelo: 'AWR300',
    origem: 'alibaba',
    fornecedor: 'Guangzhou Animal Tag Co.',
    moedaCusto: 'USD',
    custoUsd: 96,
    custoBrl: 0,
    pesoG: 210,
    comprimentoCm: 18,
    larguraCm: 8,
    alturaCm: 4,
    specs: ['FDX-B', 'Bluetooth 5.0', 'Tela OLED', 'Recarregável por USB-C'],
    imagemUrl: '',
    ativo: true,
    ordem: 2,
  },
  {
    id: 3,
    categoria: 'leitor',
    nome: 'Antena de painel para tronco',
    modelo: 'Painel 60',
    origem: 'nacional',
    fornecedor: 'Agrotag Equipamentos',
    moedaCusto: 'BRL',
    custoUsd: 0,
    custoBrl: 4890,
    pesoG: 5400,
    comprimentoCm: 65,
    larguraCm: 50,
    alturaCm: 12,
    specs: ['Leitura a até 80 cm', 'Uso em tronco e balança', 'Proteção IP67'],
    imagemUrl: '',
    ativo: true,
    ordem: 3,
  },
  {
    id: 4,
    categoria: 'brinco',
    nome: 'Brinco eletrônico FDX-B',
    modelo: 'Botão 30 mm',
    origem: 'proprio',
    fornecedor: 'Curral Tech',
    moedaCusto: 'BRL',
    custoUsd: 0,
    custoBrl: 7.9,
    pesoG: 9,
    comprimentoCm: 3,
    larguraCm: 3,
    alturaCm: 2,
    specs: ['ISO 11784/11785', 'Numeração SISBOV opcional', 'Garantia de 5 anos'],
    imagemUrl: '',
    ativo: true,
    ordem: 4,
  },
  {
    id: 5,
    categoria: 'brinco',
    nome: 'Brinco eletrônico HDX',
    modelo: 'Bandeira grande',
    origem: 'proprio',
    fornecedor: 'Curral Tech',
    moedaCusto: 'BRL',
    custoUsd: 0,
    custoBrl: 11.5,
    pesoG: 14,
    comprimentoCm: 7,
    larguraCm: 5,
    alturaCm: 2,
    specs: ['Leitura a maior distância', 'Área para numeração visual', 'Cores personalizadas'],
    imagemUrl: '',
    ativo: true,
    ordem: 5,
  },
  {
    id: 6,
    categoria: 'aplicador',
    nome: 'Alicate aplicador universal',
    modelo: 'AP-200',
    origem: 'nacional',
    fornecedor: 'Agrotag Equipamentos',
    moedaCusto: 'BRL',
    custoUsd: 0,
    custoBrl: 189,
    pesoG: 480,
    comprimentoCm: 26,
    larguraCm: 10,
    alturaCm: 4,
    specs: ['Compatível com botão e bandeira', 'Pino de aço inox'],
    imagemUrl: '',
    ativo: true,
    ordem: 6,
  },
  {
    id: 7,
    categoria: 'acessorio',
    nome: 'Capa de proteção para leitor',
    modelo: 'Capa RS420',
    origem: 'alibaba',
    fornecedor: 'Shenzhen Hopeland Technologies',
    moedaCusto: 'USD',
    custoUsd: 12,
    custoBrl: 0,
    pesoG: 120,
    comprimentoCm: 30,
    larguraCm: 10,
    alturaCm: 4,
    specs: ['Silicone resistente a impacto', 'Alça de pulso'],
    imagemUrl: '',
    ativo: true,
    ordem: 7,
  },
  {
    id: 8,
    categoria: 'acessorio',
    nome: 'Carregador veicular 12V',
    modelo: 'CV-12',
    origem: 'nacional',
    fornecedor: 'Distribuidora Pantanal',
    moedaCusto: 'BRL',
    custoUsd: 0,
    custoBrl: 49,
    pesoG: 90,
    comprimentoCm: 10,
    larguraCm: 5,
    alturaCm: 4,
    specs: ['Entrada 12 V', 'Saída USB-C'],
    imagemUrl: '',
    ativo: false,
    ordem: 8,
  },
];

const PARAMS_BASE: OrcamentoDraftData['params'] = {
  cambio: 5.42,
  iiPct: 60,
  icmsPct: 17,
  freteIntlUsd: 0,
  margemPct: 40,
  freteNacional: 0,
  descontoBrincoPct: 0,
  descontoBrincoReais: 0,
};

function orcamento(
  id: number,
  status: OrcamentoSalvo['status'],
  cliente: OrcamentoDraftData['cliente'],
  kit: OrcamentoDraftData['kit'],
  opts: {
    criado: number;
    atualizado: number;
    freteNacional: number;
    descontoBrincoPct?: number;
    software: OrcamentoDraftData['software'];
    planoId: number | 'custom';
  }
): OrcamentoSalvo {
  const params = {
    ...PARAMS_BASE,
    freteNacional: opts.freteNacional,
    descontoBrincoPct: opts.descontoBrincoPct ?? 0,
  };
  const itens = kit
    .map((k) => ({
      produto: orcamentoProdutos.find((p) => p.id === k.produtoId)!,
      quantidade: k.quantidade,
    }))
    .filter((i) => i.produto);
  const totalGeral = Math.round(calcularOrcamento(itens, params).totalGeral * 100) / 100;
  return {
    id,
    clienteNome: cliente.nome,
    clienteCidade: cliente.cidade,
    clienteUf: cliente.uf,
    totalGeral,
    status,
    createdAt: diasAtras(opts.criado, 9),
    updatedAt: diasAtras(opts.atualizado, 16),
    dados: {
      cliente,
      params,
      software: opts.software,
      planoId: opts.planoId,
      interno: { impostoVendaPct: 6, custoBrincoReais: 3.2 },
      kit,
      origem: { cep: '79002-000', local: 'Campo Grande, MS' },
    },
  };
}

export const orcamentosSalvos: OrcamentoSalvo[] = [
  orcamento(
    1,
    'enviado',
    {
      nome: 'Leandro Furtado',
      cidade: 'Cáceres',
      uf: 'MT',
      cep: '78200-000',
      animais: '850',
    },
    [
      { produtoId: 1, quantidade: 1 },
      { produtoId: 4, quantidade: 850 },
      { produtoId: 6, quantidade: 1 },
    ],
    {
      criado: 0,
      atualizado: 0,
      freteNacional: 142.6,
      descontoBrincoPct: 5,
      software: { plano: 'Profissional', mensal: 99.9, meses: 12 },
      planoId: 2,
    }
  ),
  orcamento(
    2,
    'vendido',
    {
      nome: 'Iracema Barbosa',
      cidade: 'Goiânia',
      uf: 'GO',
      cep: '74000-000',
      animais: '1100',
    },
    [
      { produtoId: 1, quantidade: 1 },
      { produtoId: 7, quantidade: 1 },
    ],
    {
      criado: 4,
      atualizado: 3,
      freteNacional: 68.4,
      software: { plano: 'Profissional', mensal: 99.9, meses: 12 },
      planoId: 2,
    }
  ),
  orcamento(
    3,
    'rascunho',
    {
      nome: 'Fazenda Rio Vermelho',
      cidade: 'Aquidauana',
      uf: 'MS',
      cep: '79200-000',
      animais: '3200',
    },
    [
      { produtoId: 1, quantidade: 2 },
      { produtoId: 3, quantidade: 1 },
      { produtoId: 5, quantidade: 3200 },
      { produtoId: 6, quantidade: 2 },
    ],
    {
      criado: 7,
      atualizado: 2,
      freteNacional: 386,
      descontoBrincoPct: 8,
      software: { plano: 'Plano personalizado', mensal: 690, meses: 24 },
      planoId: 'custom',
    }
  ),
  orcamento(
    4,
    'vendido',
    {
      nome: 'Tião Galinha',
      cidade: 'Rio Verde',
      uf: 'GO',
      cep: '75900-000',
      animais: '180',
    },
    [
      { produtoId: 2, quantidade: 1 },
      { produtoId: 4, quantidade: 200 },
      { produtoId: 6, quantidade: 1 },
    ],
    {
      criado: 19,
      atualizado: 16,
      freteNacional: 54.9,
      software: { plano: 'Básico', mensal: 39.9, meses: 12 },
      planoId: 1,
    }
  ),
  orcamento(
    5,
    'enviado',
    {
      nome: 'Agropecuária Santa Luzia',
      cidade: 'Ji-Paraná',
      uf: 'RO',
      cep: '76900-000',
      animais: '5400',
    },
    [
      { produtoId: 1, quantidade: 3 },
      { produtoId: 5, quantidade: 5400 },
      { produtoId: 6, quantidade: 3 },
      { produtoId: 7, quantidade: 3 },
    ],
    {
      criado: 33,
      atualizado: 30,
      freteNacional: 512.3,
      descontoBrincoPct: 10,
      software: { plano: 'Enterprise', mensal: 1490, meses: 12 },
      planoId: 3,
    }
  ),
];

// ---------------------------------------------------------------------------
// Notificações (banners in-app + push)
// ---------------------------------------------------------------------------

export const notificacoes: Notification[] = [
  {
    id: 1,
    type: 'update',
    title: 'Nova versão do aplicativo',
    body: 'A versão 2.8.0 traz leitura de brinco mais rápida e correções na sincronização. Atualize para continuar usando.',
    images: [],
    items: [
      'Leitura de brinco até 2x mais rápida',
      'Sincronização retoma sozinha quando volta o sinal',
      'Correção no relatório de pesagens',
    ],
    link: null,
    force: true,
    sendPush: true,
    minVersion: '2.8.0',
    targetPropertyId: null,
    startsAt: diasAtras(2, 8),
    endsAt: null,
    priority: 10,
    readCount: 187,
    lastSent: 241,
    lastFailed: 6,
    lastDispatchedAt: diasAtras(2, 8),
    createdBy: 'Rafael Antunes',
    createdAt: diasAtras(2, 7),
    updatedAt: diasAtras(2, 8),
  },
  {
    id: 2,
    type: 'ad',
    title: 'Brinco eletrônico com 10% de desconto',
    body: 'Até o fim do mês, pedidos acima de 500 brincos ganham 10% de desconto e frete grátis para MS, MT e GO.',
    images: [],
    items: [],
    link: 'https://curraltech.com/brincos',
    force: false,
    sendPush: false,
    minVersion: null,
    targetPropertyId: null,
    startsAt: diasAtras(6, 9),
    endsAt: diasAFrente(14),
    priority: 5,
    readCount: 312,
    lastSent: null,
    lastFailed: null,
    lastDispatchedAt: null,
    createdBy: 'Bruno Tavares',
    createdAt: diasAtras(6, 9),
    updatedAt: diasAtras(6, 9),
  },
  {
    id: 3,
    type: 'task',
    title: 'Vacinação contra brucelose pendente',
    body: 'Há 42 bezerras entre 3 e 8 meses sem registro da vacina de brucelose na Fazenda Três Irmãos.',
    images: [],
    items: [],
    link: null,
    force: false,
    sendPush: true,
    minVersion: null,
    targetPropertyId: 1,
    startsAt: diasAtras(1, 6),
    endsAt: diasAFrente(6),
    priority: 8,
    readCount: 3,
    lastSent: 4,
    lastFailed: 0,
    lastDispatchedAt: diasAtras(1, 6),
    createdBy: null,
    createdAt: diasAtras(1, 6),
    updatedAt: diasAtras(1, 6),
  },
  {
    id: 4,
    type: 'push',
    title: 'Sincronize antes da pesagem',
    body: 'Vai pesar o gado esta semana? Sincronize o aplicativo com internet antes de sair para o curral.',
    images: [],
    items: [],
    link: null,
    force: false,
    sendPush: true,
    minVersion: null,
    targetPropertyId: null,
    startsAt: diasAtras(9, 7),
    endsAt: null,
    priority: 3,
    readCount: 0,
    lastSent: 236,
    lastFailed: 11,
    lastDispatchedAt: diasAtras(9, 7),
    createdBy: 'Mikael David',
    createdAt: diasAtras(9, 7),
    updatedAt: diasAtras(9, 7),
  },
  {
    id: 5,
    type: 'ad',
    title: 'Webinar: IATF com controle digital',
    body: 'Na próxima quinta, às 19h, mostramos como registrar protocolos de IATF e acompanhar a taxa de prenhez pelo app.',
    images: [],
    items: ['Protocolos prontos', 'Diagnóstico de gestação', 'Relatório por touro'],
    link: 'https://curraltech.com/webinar-iatf',
    force: false,
    sendPush: false,
    minVersion: null,
    targetPropertyId: null,
    startsAt: diasAFrente(1),
    endsAt: diasAFrente(8),
    priority: 4,
    readCount: 0,
    lastSent: null,
    lastFailed: null,
    lastDispatchedAt: null,
    createdBy: 'Bruno Tavares',
    createdAt: diasAtras(0, 9),
    updatedAt: diasAtras(0, 9),
  },
];

export const deviceInsights: DeviceInsights = {
  totalDevices: 268,
  devicesWithPushToken: 247,
  activeLast30d: 213,
  inactiveLast30d: 55,
};
